// LegalPage — T-037.
// Renders legal documents (Impressum, Privacy Policy, Terms of Service)
// with generous reading measure, typographic hierarchy, and Concrete & Lime design system.

import { useState, type ReactNode, type JSX } from "react";
import { useI18n } from "../../i18n";
import { legalPages, legalPagesById, type LegalPage as LegalPageDoc } from "../../data/legalPages";

export interface LegalPageProps {
  initialDocId?: "imprint" | "privacy" | "terms";
}

/** Parses markdown inline syntax (bold, italic, code, links) into React elements */
export function parseInline(text: string): ReactNode[] {
  const nodes: ReactNode[] = [];
  // Tokenize bold **text**, code `text`, links [text](url), italic *text*
  const pattern = /(\*\*[^*]+\*\*|`[^`]+`|\[[^\]]+\]\([^)]+\)|\*[^*]+\*)/g;
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = pattern.exec(text)) !== null) {
    if (match.index > lastIndex) {
      nodes.push(text.substring(lastIndex, match.index));
    }
    const token = match[0];
    if (token.startsWith("**") && token.endsWith("**")) {
      nodes.push(
        <strong key={match.index} className="font-semibold text-white">
          {token.slice(2, -2)}
        </strong>
      );
    } else if (token.startsWith("`") && token.endsWith("`")) {
      nodes.push(
        <code
          key={match.index}
          className="rounded bg-[#222222] px-1.5 py-0.5 font-mono text-xs text-[var(--color-lime)]"
        >
          {token.slice(1, -1)}
        </code>
      );
    } else if (token.startsWith("[") && token.includes("](") && token.endsWith(")")) {
      const linkMatch = /^\[([^\]]+)\]\(([^)]+)\)$/.exec(token);
      if (linkMatch) {
        nodes.push(
          <a
            key={match.index}
            href={linkMatch[2]}
            target="_blank"
            rel="noopener noreferrer"
            className="text-[var(--color-lime)] underline underline-offset-4 hover:brightness-125"
          >
            {linkMatch[1]}
          </a>
        );
      } else {
        nodes.push(token);
      }
    } else if (token.startsWith("*") && token.endsWith("*")) {
      nodes.push(
        <em key={match.index} className="italic text-[#E0E0E0]">
          {token.slice(1, -1)}
        </em>
      );
    } else {
      nodes.push(token);
    }
    lastIndex = pattern.lastIndex;
  }

  if (lastIndex < text.length) {
    nodes.push(text.substring(lastIndex));
  }

  return nodes;
}

/** Parses markdown block structure into semantic HTML elements */
function renderMarkdownBlocks(content: string): JSX.Element[] {
  const lines = content.split("\n");
  const elements: JSX.Element[] = [];
  let listItems: string[] = [];
  let inAlert: "note" | "warning" | "caution" | null = null;
  let alertLines: string[] = [];

  const flushList = (key: string) => {
    if (listItems.length > 0) {
      elements.push(
        <ul key={key} className="my-4 list-disc space-y-2 pl-6 text-[#CCCCCC] leading-relaxed">
          {listItems.map((item, idx) => (
            <li key={idx}>{parseInline(item)}</li>
          ))}
        </ul>
      );
      listItems = [];
    }
  };

  const flushAlert = (key: string) => {
    if (alertLines.length > 0 && inAlert) {
      const borderClass =
        inAlert === "caution"
          ? "border-red-500 bg-red-950/20 text-red-200"
          : inAlert === "warning"
            ? "border-amber-500 bg-amber-950/20 text-amber-200"
            : "border-[var(--color-lime)] bg-[var(--color-slab)] text-[var(--color-ink-hi)]";
      elements.push(
        <aside
          key={key}
          className={"my-6 rounded border-l-4 p-4 font-sans text-sm leading-relaxed " + borderClass}
        >
          {alertLines.map((l, i) => (
            <p key={i} className="my-1">
              {parseInline(l)}
            </p>
          ))}
        </aside>
      );
      alertLines = [];
      inAlert = null;
    }
  };

  lines.forEach((rawLine, index) => {
    const line = rawLine.trim();

    // Alert blocks
    if (line.startsWith("> [!NOTE]")) {
      flushList("flush-list-" + index);
      inAlert = "note";
      return;
    }
    if (line.startsWith("> [!WARNING]")) {
      flushList("flush-list-" + index);
      inAlert = "warning";
      return;
    }
    if (line.startsWith("> [!CAUTION]")) {
      flushList("flush-list-" + index);
      inAlert = "caution";
      return;
    }
    if (inAlert) {
      if (line.startsWith(">")) {
        alertLines.push(line.replace(/^>\s*/, ""));
        return;
      } else {
        flushAlert("flush-alert-" + index);
      }
    }

    // Horizontal Rule
    if (line === "---") {
      flushList("flush-list-" + index);
      elements.push(<hr key={"hr-" + index} className="my-8 border-t border-[var(--color-line)]" />);
      return;
    }

    // Headings
    if (line.startsWith("# ")) {
      flushList("flush-list-" + index);
      elements.push(
        <h1
          key={"h1-" + index}
          className="mb-8 mt-4 font-display text-4xl uppercase tracking-[0.02em] text-[var(--color-lime)] sm:text-5xl"
        >
          {line.replace(/^#\s+/, "")}
        </h1>
      );
      return;
    }
    if (line.startsWith("## ")) {
      flushList("flush-list-" + index);
      elements.push(
        <h2
          key={"h2-" + index}
          className="mb-4 mt-10 border-b border-[var(--color-line)] pb-2 font-display text-2xl uppercase tracking-[0.03em] text-[var(--color-ink-hi)] sm:text-3xl"
        >
          {line.replace(/^##\s+/, "")}
        </h2>
      );
      return;
    }
    if (line.startsWith("### ")) {
      flushList("flush-list-" + index);
      elements.push(
        <h3
          key={"h3-" + index}
          className="mb-3 mt-6 font-subhead text-lg uppercase tracking-[0.04em] text-[var(--color-lime)]"
        >
          {line.replace(/^###\s+/, "")}
        </h3>
      );
      return;
    }

    // List items
    if (line.startsWith("* ") || line.startsWith("- ") || /^\d+\.\s+/.test(line)) {
      listItems.push(line.replace(/^(\*|-|\d+\.)\s+/, ""));
      return;
    }

    // Empty lines
    if (!line) {
      flushList("flush-list-" + index);
      return;
    }

    // Regular paragraphs
    flushList("flush-list-" + index);
    elements.push(
      <p key={"p-" + index} className="my-4 font-sans text-base leading-relaxed text-[#CCCCCC]">
        {parseInline(line)}
      </p>
    );
  });

  flushList("flush-list-final");
  flushAlert("flush-alert-final");
  return elements;
}

export function LegalPage({ initialDocId = "privacy" }: LegalPageProps): JSX.Element {
  const { t, locale } = useI18n();
  const [activeDocId, setActiveDocId] = useState<"imprint" | "privacy" | "terms">(initialDocId);

  const doc: LegalPageDoc = legalPagesById[activeDocId] || legalPages[0];
  const content = doc.content[locale] || doc.content.en;

  const docNavLabels: Record<"imprint" | "privacy" | "terms", string> = {
    imprint: t("landing.footer.imprint") || "Impressum",
    privacy: t("landing.footer.privacy") || "Datenschutz",
    terms: t("landing.footer.terms") || "AGB",
  };

  return (
    <div className="min-h-screen bg-[var(--color-void)] text-[var(--color-ink)] selection:bg-[var(--color-lime)] selection:text-[var(--color-void)]">
      <header className="sticky top-0 z-40 border-b border-[var(--color-line)] bg-[var(--color-void)]/90 backdrop-blur-md">
        <div className="mx-auto flex h-16 max-w-3xl items-center justify-between px-6 sm:px-8">
          <a
            href="/"
            className="flex items-center gap-2 font-display text-xl uppercase tracking-[0.02em] text-[var(--color-lime)] hover:brightness-125"
          >
            <span>←</span>
            <span>{t("brand.name")}</span>
          </a>
          <span className="font-subhead text-xs uppercase tracking-[0.08em] text-[var(--color-ink-mute)]">
            {t("landing.footer.legal") || "Legal"}
          </span>
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-6 py-12 sm:px-8">
        <nav
          aria-label="legal documents"
          className="mb-10 flex flex-wrap gap-2 border-b border-[var(--color-line)] pb-4"
        >
          {legalPages.map((p) => {
            const isSelected = p.id === activeDocId;
            return (
              <button
                key={p.id}
                type="button"
                onClick={() => setActiveDocId(p.id)}
                aria-pressed={isSelected}
                className={
                  "px-4 py-2 font-subhead text-sm uppercase tracking-[0.06em] transition-colors duration-[120ms] " +
                  (isSelected
                    ? "bg-[var(--color-lime)] font-semibold text-[var(--color-void)]"
                    : "border border-[var(--color-line)] text-[var(--color-ink-mute)] hover:border-[var(--color-ink-mute)] hover:text-white")
                }
                style={{ borderRadius: "var(--radius-sharp)", minHeight: "44px" }}
              >
                {docNavLabels[p.id]}
              </button>
            );
          })}
        </nav>

        <article className="prose-concrete max-w-[70ch] leading-relaxed">
          <header className="mb-6">
            <p className="font-subhead text-xs uppercase tracking-[0.08em] text-[var(--color-ink-mute)]">
              Last Updated: {doc.lastUpdated}
            </p>
          </header>

          <div className="space-y-2">{renderMarkdownBlocks(content)}</div>
        </article>
      </main>
    </div>
  );
}

export default LegalPage;
